import React, { useEffect, useState } from "react";
import { Stack } from "react-bootstrap";
import {
  ConfigCurrency,
  FiatCurrency,
} from "@nestcoinco/hyper-api-gateway-api-client";

import "./SelectCurrencyModal.scss";
import BottomDrawer from "../../BottomDrawer/BottomDrawer";
import PrimaryActionButton from "../../Buttons/PrimaryActionButton/PrimaryActionButton";
import WarningBadge from "../../WarningBadge/WarningBadge";
import Combobox, { SelectedComboboxOption } from "../../Combobox/Combobox";
import { getSupportedCurrencies } from "../../../api-clients/configurationsClient";
import { handleUpdateUser } from "../../../api-clients/userAccountClient";
import { showSuccessToast } from "../../../utils/show-toast";
import { CustomSpinner } from "../../CustomSpinner/CustomSpinner";
import { GenericModalProps } from "../../../utils/constants/generic-types";
import Skeleton from "../../Skeleton/Skeleton";
import { useSetAtom } from "jotai";
import { userProfileAtom } from "../../../jotai/atoms/user";
import useAnalytics from "../../../hooks/useAnalytics";

interface SelectCurrencyModalProps extends GenericModalProps {
  handleSuccess: (updatedCurrency: FiatCurrency) => void;
}

export default function SelectCurrencyModal({
  isOpen,
  onClose,
  handleSuccess,
}: SelectCurrencyModalProps & GenericModalProps) {
  const setUserProfile = useSetAtom(userProfileAtom);
  const [loading, setLoading] = useState<boolean>(false);
  const [currencyList, setCurrencyList] = useState<ConfigCurrency[]>();
  const [selectedCurrency, setSelectedCurrency] = useState<FiatCurrency>();
  const analytics = useAnalytics();

  const changeCurrency = (selectedOption: SelectedComboboxOption) => {
    setSelectedCurrency(selectedOption.value[0] as FiatCurrency);
  };

  const updateUserCurrency = async () => {
    setLoading(true);

    try {
      const userData = await handleUpdateUser({
        currency: selectedCurrency,
      });
      if (userData) {
        analytics.sendSelectCurrencyEvent(selectedCurrency);
        showSuccessToast("Currency updated!");
        handleSuccess(selectedCurrency as FiatCurrency);
        setUserProfile(userData);
        onClose();
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      const currencies = await getSupportedCurrencies();
      setCurrencyList(currencies);
    })();
  }, []);

  return (
    <>
      <BottomDrawer
        isOpen={isOpen}
        onClose={onClose}
        containerClass="SelectCurrencyModal"
      >
        <div className="pt-2 d-flex flex-column justify-content-between ModalContent">
          <Stack gap={4}>
            <Stack gap={1} className="Copy">
              <h1 className="Hanson-700 Heading">Select Default Currency</h1>
              <p className="Grotesk SubCopy">
                This would be used for <span className="fw-bold">deposits</span>{" "}
                and <span className="fw-bold">cash outs</span>.
              </p>
              <WarningBadge text="You CANNOT change this after your first top-up." />
            </Stack>
            <Skeleton isLoading={!currencyList} height={40} borderRadius={12}>
              <Combobox
                inputLabel="Default currency"
                inputProps={{ placeholder: "Select currency", required: true }}
                options={currencyList || []}
                handleSelection={changeCurrency}
              />
            </Skeleton>
          </Stack>

          <PrimaryActionButton
            size="lg"
            text={!loading ? "Continue" : <CustomSpinner size="sm" />}
            disabled={!selectedCurrency || loading}
            onClick={updateUserCurrency}
          />
        </div>
      </BottomDrawer>
    </>
  );
}
